import { z } from 'zod'
import { useResourcesStore } from '@/stores/resources'
import { useChatStore } from '@/stores/chat'
import { useRoomStore } from '@/stores/room'
import {
  CHANNEL_TYPE,
  INCOMING_CALL_CONTEXT,
  REDIRECT_PATHS,
  DIALER_CALL_CONTEXT,
} from '@/helpers/constants'
import type {
  CallError,
  CallErrorType,
  ChannelType,
  RedirectPath,
} from '@/helpers/types'
import { redirect } from '@tanstack/react-router'
import {
  dispatchDeviceErrorDialog,
  dispatchHandleRoomJoinErrorDialog,
  dispatchMissingContextErrorDialog,
} from '@/helpers/errorDialogMessages'
import {
  refreshDevices,
  mapRejectedDevicesToErrorType,
  isSDKErrorHTTP404,
  formatTimerFromSeconds,
  waitAndRetry,
} from '@/helpers/utils'
import { logger } from '@/logger/createLogger'
import { useRoomSessionStore } from '@/stores/roomSession'
import { useUiStore } from '@/stores/ui'

// TODO: does zod have a way to access the default values from the schema?
export const ROOM_SEARCH_SCHEMA_DEFAULTS = {
  CHANNEL: CHANNEL_TYPE.AUDIO,
  REDIRECT: REDIRECT_PATHS.RECENT,
} as const

export const KNOWN_RESOURCE_CONTEXT = {
  EXTERNAL: 'external',
  GUEST: 'guest',
  PRIVATE: 'private',
  PUBLIC: 'public',
} as const

const knownResourceContextSchema = z.enum([
  KNOWN_RESOURCE_CONTEXT.EXTERNAL,
  KNOWN_RESOURCE_CONTEXT.GUEST,
  KNOWN_RESOURCE_CONTEXT.PRIVATE,
  KNOWN_RESOURCE_CONTEXT.PUBLIC,
])
const knownAppContextSchema = z.enum([
  INCOMING_CALL_CONTEXT, // for incoming calls
  DIALER_CALL_CONTEXT, // for dialer calls
])

export const roomContextSchema = z.union([
  knownResourceContextSchema,
  knownAppContextSchema,
])

type KnownResourceContext = z.infer<typeof knownResourceContextSchema>
type KnownAppContext = z.infer<typeof knownAppContextSchema>
export type UnknownRoomContext = string & {} // allows any string but provides auto-completion

export type RoomContext =
  | KnownAppContext
  | KnownResourceContext
  | UnknownRoomContext

export const roomSearchSchema = z.object({
  channel: z
    .enum([CHANNEL_TYPE.AUDIO, CHANNEL_TYPE.VIDEO])
    .optional()
    .catch(error => {
      logger.error('Invalid channel provided', error)
      // fallback to audio channel
      return ROOM_SEARCH_SCHEMA_DEFAULTS.CHANNEL
    }),
  redirect: z
    .enum([
      REDIRECT_PATHS.CALLS,
      REDIRECT_PATHS.CHATS,
      REDIRECT_PATHS.DIRECTORY,
      REDIRECT_PATHS.RECENT,
      REDIRECT_PATHS.ROOMS,
    ])
    .optional()
    .catch(error => {
      logger.error('Invalid redirect path provided', error)
      // fallback to recent path
      return ROOM_SEARCH_SCHEMA_DEFAULTS.REDIRECT
    }),
})

interface RoomBeforeLoadParams {
  context: string
  name: string
}
// TODO: Remove as a helper and have this function live in the route/router file(s)?
export const roomBeforeLoad = async ({
  context,
  name,
}: RoomBeforeLoadParams) => {
  const ATTEMPT_COUNT_MAX = 100 // 10 seconds
  const ATTEMPT_DELAY_MS = 100 // 100ms

  const routeContext = {
    title: `${context}/${name}`,
  }

  // FIXME: this is a hack to ensure the CallSession/RoomSession is not defined before joining a room
  // this means the previous room has to hangup before the new room can join
  // this also ensures that a "ghost" room is not left behind if the user navigates away from the room
  const hasCallSession = await waitAndRetry({
    delayMs: ATTEMPT_DELAY_MS,
    fn: () => useRoomSessionStore.getState().roomSession,
    maxAttempts: ATTEMPT_COUNT_MAX,
    predicate: callSession => callSession === undefined,
  })
  if (hasCallSession) {
    const roomSession = useRoomSessionStore.getState().roomSession
    logger.error(
      'Room session already exists which should not happen when joining a room',
      roomSession,
    )
    throw new Error(
      `Waited for previous room to hangup for ${ATTEMPT_COUNT_MAX * ATTEMPT_DELAY_MS}ms but it did not hangup`,
    )
  }
  return routeContext
}

const requestDevicePermissions = async (channel: ChannelType) => {
  const audioOnly = ['microphone', 'speaker'] as const
  const video = ['camera', 'microphone', 'speaker'] as const
  const devicesToRefresh = channel === CHANNEL_TYPE.VIDEO ? video : audioOnly

  // refresh and/or request permissions for required devices
  const deviceErrors = await refreshDevices([...devicesToRefresh])

  const rejectDevices = mapRejectedDevicesToErrorType(deviceErrors)

  // TODO: handle all device types requiring permissions for better UX feedback
  const [firstErrorType] = rejectDevices

  return firstErrorType?.errorType
}

interface RoomLoaderParams {
  afterCallLeaveFn?: (() => void) | undefined
  afterCallStartFn?: (() => void) | undefined
  beforeCallLeaveFn?: (() => boolean) | undefined
  channel: ChannelType
  name: string
  redirect: RedirectPath
  userParameters?: Record<string, unknown>
}

export const roomLoader = async ({
  afterCallLeaveFn,
  afterCallStartFn,
  beforeCallLeaveFn,
  channel,
  name,
  redirect: redirectPath,
  userParameters = {},
}: RoomLoaderParams) => {
  logger.debug('>> roomLoader')

  const { setRedirect } = useRoomStore.getState().actions

  setRedirect(redirectPath)

  // Request or refresh device list prior to joining the room
  const deviceErrorType = await requestDevicePermissions(channel)

  // show device error dialog if needed
  if (deviceErrorType) {
    // TODO: allow the user to retry or join without required device permission
    dispatchDeviceErrorDialog({
      errorType: deviceErrorType,
    })

    // redirect via router
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: redirectPath,
    })
  }

  const { listenCallLeft, listenCallStarted, allowLeaveCall } =
    useRoomSessionStore.getState().actions

  // Set Session Callbacks
  listenCallLeft(() => afterCallLeaveFn?.())
  listenCallStarted(() => afterCallStartFn?.())
  allowLeaveCall(() => (beforeCallLeaveFn ? beforeCallLeaveFn() : true))

  // Fetch the resource by the name to get room's chat conversations
  const { fetchResource } = useResourcesStore.getState().actions

  void fetchResource({ name })
    .then(resource => {
      const { setAddressId, setRoomName, setUserParameters } =
        useRoomStore.getState().actions

      const { getChatMessages, subscribeToChatMessages } =
        useChatStore.getState().actions

      // Set the room info in the store
      setRoomName(resource.name)
      setAddressId(resource.id)
      setUserParameters(userParameters)

      // subscribe and fetch initial chat messages and page cursors (if exists)
      subscribeToChatMessages({
        addressId: resource.id,
        onMessage: message =>
          logger.debug('Received new chat message:', message),
      }).catch(error => {
        logger.error('Error subscribing to chat messages:', error)
        throw error
      })

      getChatMessages({ addressId: resource.id }).catch(error => {
        logger.error('Error fetching chat messages:', error)
        throw error
      })
    })
    .catch(error => {
      if (isSDKErrorHTTP404(error)) {
        logger.warn(
          'Resource not found, fallback to room name and user parameters',
        )

        // fallback to setting the room name and user parameters
        const { setRoomName, setUserParameters } =
          useRoomStore.getState().actions

        setRoomName(name)
        setUserParameters(userParameters)
      } else {
        logger.error('Error fetching resource for room:', error)
        // re-throw other errors
        throw error
      }
    })
}

type CallErrorMap = {
  [K in CallErrorType]: (callError: Extract<CallError, { type: K }>) => void
}
const handleCallError = (callError: CallError, onConfirm: () => void) => {
  const callErrorMap = {
    dial: () => {
      dispatchHandleRoomJoinErrorDialog({
        onConfirm,
      })
    },
    prejoin: params => {
      dispatchMissingContextErrorDialog({
        onConfirm,
        type: params.message,
      })
    },
  } satisfies CallErrorMap

  switch (callError.type) {
    case 'prejoin':
      callErrorMap.prejoin(callError)
      break
    case 'dial':
      callErrorMap.dial()
  }
}

export const roomOnLeave = async () => {
  const ATTEMPT_COUNT_MAX = 100 // 10 seconds
  const ATTEMPT_DELAY_MS = 100 // 100ms
  logger.debug('roomOnLeave')

  const { addressId, callState, elapsedTimeSecs, callError } =
    useRoomStore.getState()
  const { _cleanUp, leaveRoom, setCallState } = useRoomStore.getState().actions
  const { showCallEndDialog, setCallEndDialogContent } =
    useUiStore.getState().actions

  // FIXME: this is a hack to ensure the CallSession/RoomSession is defined and call.start() is resolved
  // before calling hangup and cleanup
  const hasCallSession = await waitAndRetry({
    delayMs: ATTEMPT_DELAY_MS,
    fn: () => useRoomSessionStore.getState().roomSession,
    maxAttempts: ATTEMPT_COUNT_MAX,
    predicate: callSession => Boolean(callSession) === true,
  })
  if (!hasCallSession) {
    logger.error(
      'Room session undefined which should not happen when leaving a room',
    )
    // Unsubscribe from chat messages
    useChatStore.getState().actions.unsubscribeChatMessages(addressId)
    // Clean up the room
    _cleanUp()
    throw new Error(
      `Waited for the room to finish dialing for ${ATTEMPT_COUNT_MAX * ATTEMPT_DELAY_MS}ms but it did not finish dialing`,
    )
  }

  // NOTE: set the call state as leaving to prevent the onDestroy from being called again which
  // would cause roomOnLeave to be called again. Being called twice can cause timing issues if a new call is started
  setCallState('leaving')

  if (callError.type) {
    handleCallError(callError, _cleanUp)
  } else if (callState !== 'switching') {
    // show the call end dialog if there is
    // no call error and the call is not switching (change rooms/ client-side transfer)
    showCallEndDialog()
    setCallEndDialogContent({
      description: `Time total: ${formatTimerFromSeconds(elapsedTimeSecs)}`,
    })
  }

  // the leaveRoom can potentially return an error
  void leaveRoom().then(result => {
    if (result.error) {
      // TODO: gracefully handle leave/hangup error
      logger.error('Error leaving room:', result.error)
      // TODO: always show toast or only on certain errors?
      // dispatchUnhandledErrorToast()
    }

    // Unsubscribe from chat messages
    useChatStore.getState().actions.unsubscribeChatMessages(addressId)
    // Clean up the room
    _cleanUp()
    logger.debug('roomOnLeave done')
  })
}
